import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation } from 'wouter';
import { api } from '../lib/api';
import { todayInput } from '../lib/format';
import { fieldErrors } from '../lib/form-errors';
import { ORDER_TAGS_LIMIT, parseTagInput, tagInputError } from '../lib/order-tags';
import { haptic } from '../lib/telegram';
import type { ArtistProfile, Client, ClientListItem, ListResponse, Order, Service } from '../types';
import { NewClientModal } from '../pages/clients-page';
import { Button, ErrorNotice, Field, Input, Modal, Select } from '../components/ui';

const ORDER_FIELD_LABELS: Record<string, string> = {
  clientId: 'Клиент',
  title: 'Название',
  description: 'Описание',
  totalPrice: 'Стоимость',
  depositRequired: 'Предоплата',
  currency: 'Валюта',
  deadline: 'Дедлайн',
  tags: 'Теги',
};

/**
 * Ручное создание заказа: не всякая работа приходит через публичную форму.
 * Клиент выбирается из базы или заводится тут же, а значения услуги
 * подставляются как черновик — художник правит их под конкретный заказ.
 * Тарифный лимит проверяет сервер в той же транзакции, что и создание.
 */
export function NewOrderModal({ onClose }: { onClose: () => void }) {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [clientSearch, setClientSearch] = useState('');
  const [clientId, setClientId] = useState('');
  const [addingClient, setAddingClient] = useState(false);
  const [serviceId, setServiceId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [totalPrice, setTotalPrice] = useState('');
  const [deposit, setDeposit] = useState('');
  const [currency, setCurrency] = useState('');
  const [deadline, setDeadline] = useState('');
  const [tags, setTags] = useState('');

  const profile = useQuery({
    queryKey: ['profile'],
    queryFn: () => api<ArtistProfile>('/artist/profile'),
  });
  const services = useQuery({
    queryKey: ['services'],
    queryFn: () => api<{ items: Service[] }>('/services'),
  });
  const clients = useQuery({
    queryKey: ['clients', clientSearch.trim(), 0],
    queryFn: () =>
      api<ListResponse<ClientListItem>>(
        `/clients?limit=20&offset=0${clientSearch.trim() ? `&query=${encodeURIComponent(clientSearch.trim())}` : ''}`,
      ),
  });
  const currencies = profile.data?.currencies ?? [];
  const activeCurrency = currency || currencies[0] || 'RUB';
  const create = useMutation({
    mutationFn: () =>
      api<Order>('/orders', {
        method: 'POST',
        body: {
          clientId,
          serviceId: serviceId || null,
          title: title.trim(),
          description: description.trim(),
          totalPrice,
          currency: activeCurrency,
          depositRequired: deposit || '0',
          deadline: deadline || null,
          tags: parseTagInput(tags),
        },
      }),
    onSuccess: (order) => {
      haptic('success');
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: ['queue'] });
      onClose();
      // Сразу открываем карточку: дальше художник ведёт заказ уже в ней.
      navigate(`/orders/${order.id}`);
    },
  });
  const errors = fieldErrors(create.error);
  const tagsError = tagInputError(tags) ?? errors.tags;
  const depositError =
    Number(deposit || 0) > Number(totalPrice || 0)
      ? 'Предоплата не может быть больше стоимости'
      : errors.depositRequired;

  /** Услуга задаёт черновик цены, срока и предоплаты — дальше правит художник. */
  const applyService = (id: string) => {
    setServiceId(id);
    const service = services.data?.items.find((item) => item.id === id);
    if (!service) return;

    if (!title.trim()) setTitle(service.title);
    if (!description.trim() && service.description) setDescription(service.description);
    if (service.basePrice) {
      setTotalPrice(service.basePrice);
      const percent = service.depositPercent ?? profile.data?.defaultDepositPercent ?? 0;
      setDeposit(((Number(service.basePrice) * percent) / 100).toFixed(2));
    }
    setCurrency(service.currency);
    if (service.durationMaxDays) {
      setDeadline(addDays(service.durationMaxDays));
    }
  };

  return (
    <Modal title="Новый заказ" onClose={onClose}>
      <form
        // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
        noValidate
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Поиск клиента" hint="Имя, ник или Telegram ID">
          <Input
            value={clientSearch}
            placeholder="Анна"
            onChange={(event) => setClientSearch(event.target.value)}
          />
        </Field>
        <Field label="Клиент" error={errors.clientId}>
          <Select value={clientId} onChange={(event) => setClientId(event.target.value)}>
            <option value="">Выберите из базы</option>
            {clients.data?.items.map((client) => (
              <option key={client.id} value={client.id}>
                {client.displayName}
                {client.username ? ` (@${client.username})` : ''}
                {client.isBlocked ? ' — заблокирован' : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Button variant="ghost" onClick={() => setAddingClient(true)}>
          Клиента нет в базе — добавить
        </Button>

        {services.data && services.data.items.length > 0 && (
          <Field label="Услуга" hint="Подставит цену, срок и предоплату">
            <Select value={serviceId} onChange={(event) => applyService(event.target.value)}>
              <option value="">Без услуги</option>
              {services.data.items.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.title}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label="Название" error={errors.title}>
          <Input
            value={title}
            placeholder="Портрет персонажа"
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>
        <Field label="Описание" error={errors.description}>
          <textarea
            className="input input--textarea"
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <div className="form-grid">
          <Field label={`Стоимость, ${activeCurrency}`} error={errors.totalPrice}>
            <Input
              inputMode="decimal"
              value={totalPrice}
              onChange={(event) => setTotalPrice(event.target.value)}
            />
          </Field>
          <Field label="Предоплата" error={depositError}>
            <Input
              inputMode="decimal"
              value={deposit}
              onChange={(event) => setDeposit(event.target.value)}
            />
          </Field>
        </div>
        {currencies.length > 1 && (
          <Field label="Валюта" error={errors.currency}>
            <Select value={activeCurrency} onChange={(event) => setCurrency(event.target.value)}>
              {currencies.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <div className="form-grid">
          <Field label="Дедлайн" hint="Пусто — работаете без срока" error={errors.deadline}>
            <Input
              type="date"
              min={todayInput()}
              value={deadline}
              onChange={(event) => setDeadline(event.target.value)}
            />
          </Field>
          <Field label="Теги" hint={`До ${ORDER_TAGS_LIMIT} через запятую`} error={tagsError}>
            <Input value={tags} onChange={(event) => setTags(event.target.value)} />
          </Field>
        </div>

        {create.error && <ErrorNotice error={create.error} labels={ORDER_FIELD_LABELS} />}
        <Button
          type="submit"
          wide
          disabled={
            !clientId ||
            !title.trim() ||
            !description.trim() ||
            !totalPrice ||
            Boolean(tagsError) ||
            Boolean(depositError) ||
            create.isPending
          }
        >
          {create.isPending ? 'Создаём…' : 'Создать заказ'}
        </Button>
      </form>

      {addingClient && (
        <NewClientModal
          onClose={() => setAddingClient(false)}
          onCreated={(client: Client) => {
            // Новый клиент сразу выбран: возвращаться к поиску не нужно.
            setClientSearch(client.displayName);
            setClientId(client.id);
          }}
        />
      )}
    </Modal>
  );
}

function addDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}
